package com.dotdrives.client

import com.dotdrives.config.BuildKonfig
import com.dotdrives.model.*
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.postgrest
import io.ktor.client.HttpClient
import io.ktor.client.request.*
import io.ktor.client.statement.bodyAsText
import io.ktor.http.*
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.*

class DonorClient(
    private val client: SupabaseClient = SupabaseProvider.client,
    private val baseUrl: String = BuildKonfig.SUPABASE_URL,
    private val publicKey: String = BuildKonfig.SUPABASE_PUBLISHABLE_KEY,
    private val httpFactory: () -> HttpClient = { HttpClient() },
) {
    private val json = Json {
        ignoreUnknownKeys = true
        explicitNulls = false
    }

    suspend fun organizations(): List<DonorOrganization> =
        client.postgrest.rpc("donor_contexts").decodeAs()

    suspend fun page(orgId: String, after: String?): DonorSnapshot =
        client.postgrest
            .rpc(
                "donor_portal",
                buildJsonObject {
                    put("p_org", orgId)
                    put("p_after", after)
                    put("p_limit", 10)
                },
            )
            .decodeAs()

    suspend fun updates(orgId: String): List<DonorUpdate> =
        client.postgrest.rpc("donor_updates", buildJsonObject { put("p_org", orgId) }).decodeAs()

    suspend fun openUpdate(orgId: String, id: String) {
        client.postgrest.rpc(
            "donor_open_update",
            buildJsonObject {
                put("p_org", orgId)
                put("p_report", id)
            },
        )
    }

    suspend fun saveContact(orgId: String, phone: String, address: String) {
        client.postgrest.rpc(
            "donor_update_contact",
            buildJsonObject {
                put("p_org", orgId)
                put("p_phone", phone)
                put("p_address", address)
            },
        )
    }

    suspend fun attachment(orgId: String, id: String): DocumentDownload =
        json.decodeFromJsonElement(
            edge(
                "workflows",
                buildJsonObject {
                        put("action", "donor.attachment")
                        put("org_id", orgId)
                        put("file_id", id)
                    }
                    .toString(),
            )
        )

    suspend fun document(id: String, retry: Boolean): DocumentDownload =
        json.decodeFromJsonElement(
            edge(
                "workflows",
                buildJsonObject {
                        put("action", if (retry) "statement.retry" else "statement.download")
                        put("statement_id", id)
                    }
                    .toString(),
            )
        )

    suspend fun recurring(request: DonorRecurringRequest): DonorRecurringResult =
        json.decodeFromJsonElement(edge("stripe-recurring", json.encodeToString(request)))

    private suspend fun edge(endpoint: String, body: String): JsonElement {
        val jwt =
            client.auth.currentSessionOrNull()?.accessToken ?: error("Sign in again to continue.")
        val http = httpFactory()
        try {
            val response =
                http.post("${baseUrl.trimEnd('/')}/functions/v1/$endpoint") {
                    header("apikey", publicKey)
                    bearerAuth(jwt)
                    contentType(ContentType.Application.Json)
                    setBody(body)
                }
            val envelope = json.parseToJsonElement(response.bodyAsText()).jsonObject
            if (!response.status.isSuccess()) {
                val code = envelope["error"]?.jsonObject?.get("code")?.jsonPrimitive?.content
                if (endpoint == "stripe-recurring" && code == "recurring_rejected") throw RecurringRequestRejected()
                error(
                    when (code) {
                        "service_unavailable",
                        "provider_unavailable",
                        "stripe_not_configured" ->
                            "Online management is unavailable. Please contact the organization or retry later."
                        "forbidden",
                        "unauthorized" -> "This item is unavailable for your signed-in account."
                        else -> "The request could not be completed. Retry the same request."
                    }
                )
            }
            return envelope["data"]
                ?: error("The service returned an incomplete response. Please retry.")
        } finally {
            http.close()
        }
    }
}
